import { useGetMe } from 'api-hooks/auth/query';
import { useGetEnums } from 'api-hooks/common';
import { CustomerBillingModel, CustomerModel } from 'api-hooks/customer';
import { FlightModel } from 'api-hooks/tour/tour-list/flight/model';
import {
  TourModel,
  TourPriceModel,
} from 'api-hooks/tour/tour-list/tour-detail';
import { Option } from 'baseui/select';
import { themedStyled } from 'common/styles/styletron.theme';
import { capitalize, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import useDialog from 'hooks/use-dialog';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import EmployeeSelectInput from 'legacy-modules/master/select-input/employee-select-input';
import CustomerBillingSelectInput from 'legacy-modules/sales/component/customer-billing-select-input';
import CustomerSalesWarningToast from 'legacy-modules/sales/component/customer-sales-warning-toast';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import NoticeSuspendDialog from 'legacy-modules/sales/notice-suspend-dialog';
import {
  SectionContainer as RawSectionContainer,
  CardSection,
  FullContainer,
  HalfContainer,
  OneThirdContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import TourListSelectInput from 'legacy-modules/tour/tour-list/components/select-input';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { TourSaleItemFormType } from './form-type';
import { addNewItem } from '../hooks/use-get-default-values';

interface Props {}

function CustomerTransformer(item: CustomerBillingModel) {
  return {
    id: item.id,
    label: transformLabel([item.name, item.address, item.phone]),
    extra: item,
  };
}

function FlightTransformer(item: FlightModel): Option {
  return {
    id: item.id,
    label: item.name,
    extra: item,
  };
}

function TypeTransformer(item: TourPriceModel): Option {
  return {
    id: item.id,
    label: `${capitalize(item.type || '')}`,
    extra: item,
  };
}

export function SalesDetailCard(props: Props) {
  const { t } = useTranslation();

  const { control, setValue, getValues } = useFormContext();
  const { editable } = useFormState();
  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const isEdit = getValues('tourSale');
  const dialog = useDialog();

  const [customerId, tourId, total, remainingTransactionQuota] = useWatch({
    control,
    name: ['customerId', 'tourId', 'total', 'remainingTransactionQuota'],
  });

  const showLimitTransactionDialog = React.useCallback(() => {
    dialog.showCustom({
      render: (close) => <NoticeSuspendDialog onClose={close} type="limited" />,
    });
  }, [dialog]);

  const isExceedingQuota =
    editable && remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    if (isExceedingQuota) {
      showLimitTransactionDialog();
    }
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, showLimitTransactionDialog, isExceedingQuota]);

  React.useEffect(() => {
    if (!customerId || customerId === '') {
      setValue('customerBillingOptions', []);
      setValue('customerBillingId', '');
      setValue('address', '');
      setValue('phone', '');
      setValue('hasDueReceivable', false);
      setValue('customerStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [customerId, setValue]);

  const onAfterDetailChangeCustomer = React.useCallback(
    (params: CustomerModel) => {
      const billing = params.billings.map((item) => {
        return CustomerTransformer(item);
      });
      const remainingTransactionQuota = params.system.remainingTransactionQuota;
      const isExceedingQuota =
        params.system.isTransactionLimited && total > remainingTransactionQuota;

      setValue('phone', params.phone);
      setValue('address', params.address);
      setValue('customerBillingOptions', billing);
      setValue('isExceedingQuota', isExceedingQuota);
      setValue('remainingTransactionQuota', remainingTransactionQuota || null);

      if (editable) {
        if (isExceedingQuota) showLimitTransactionDialog();

        setValue('customerBillingId', billing?.[0]?.id ?? '');
        setValue('hasDueReceivable', params.hasDueReceivables);
        setValue('customerStatus', params.status);
      }
    },
    [editable, setValue, showLimitTransactionDialog, total],
  );

  React.useEffect(() => {
    if (!tourId || tourId === '') {
      setValue('tourRefId', '');
      setValue('tourSales', [addNewItem()]);
      setValue('operatorId', '');
      setValue('salesmanId', '');
      setValue('adminId', '');
      setValue('tourType', '');
      setValue('typeOptions', '');
      setValue('scheduleOptions', '');
    }
  }, [setValue, tourId]);

  const onAfterDetailChangeTour = React.useCallback(
    (params: TourModel) => {
      const types =
        params?.tourPrices?.map((item) => {
          return TypeTransformer(item);
        }) ?? [];

      const tourFlights =
        params.tourFlights?.map((item) => {
          return FlightTransformer(item);
        }) ?? [];

      const options = {
        shouldValidate: true,
        shouldTouch: true,
      };

      if (editable) {
        const type = types.find((type) => type.extra.isDefault) ?? types?.[0];
        const ts = getValues('tourSales') as TourSaleItemFormType[];
        const _tourSales = ts.length ? ts : [addNewItem()];

        const enumOptions = getValues('enumOptions');

        const tourSales: TourSaleItemFormType[] = _tourSales.map((ts) => {
          const tourSaleGuests: TourSaleItemFormType['tourSaleGuests'] =
            ts.tourSaleGuests.map((tsg) => {
              const flight = tourFlights?.[0]?.extra as FlightModel | undefined;
              const bedType = enumOptions?.[0]?.id || '';
              return {
                ...tsg,
                price: type?.extra?.price || undefined,
                tourPriceId: type?.extra?.id || '',
                type: type?.extra?.label || '',
                tourPriceRefId: type?.extra?.refId || '',
                flightName: flight?.name || '',
                tourFlightId: flight?.id || '',
                tourFlightRefId: flight?.refId || '',
                bedType,
              };
            });
          return {
            customId: ts.customId,
            id: ts.id,
            tourSaleAddOns: ts.tourSaleAddOns,
            tourSaleGuests,
          };
        });

        setValue('tourId', params.id, options);
        setValue('tourRefId', params.refId, options);
        setValue('tourSales', tourSales, options);
        setValue('operatorId', params.operator?.id || '', options);
        setValue('salesmanId', params.salesman?.id || '', options);
        setValue('adminId', params.admin?.id || employee?.id || '', options);
      }

      setValue('tourType', params.type);
      setValue('typeOptions', types);
      setValue('scheduleOptions', tourFlights);
    },
    [editable, employee?.id, getValues, setValue],
  );

  const { data } = useGetEnums({
    class: 'tour-bed-type',
  });

  const [initiated, setInitiated] = React.useState(false);

  React.useEffect(() => {
    if (initiated) return;
    if (!data?.data) return;
    const options: Option[] = data.data.map((item) => {
      return {
        extra: item,
        id: item.value,
        label: item.label,
      };
    });
    setValue('enumOptions', options);
    setInitiated(true);
  }, [data?.data, initiated, setValue]);

  const tourType = getValues('tourType');

  return (
    <CardSection>
      <Text variant="HeadingXSmall">{t('ticketSales:sales-details')}</Text>
      <SeparatorVertical $margin={24} />
      <SectionContainer>
        {isEdit && (
          <HalfContainer>
            <Input
              type="text"
              name="number"
              label={t('common:invoice_number')}
              placeholder={t('common:invoice_number')}
              required
              disabled
            />
          </HalfContainer>
        )}
        <HalfContainer
          $style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <TransactionAt
            name="transactionAt"
            clearable={false}
            required
            label={t('common:transaction_at')}
            placeholder={t('common:transaction_at')}
          />
        </HalfContainer>
      </SectionContainer>
      <HalfContainer>
        <TourListSelectInput
          name="tourId"
          required
          onAfterDetailChange={onAfterDetailChangeTour}
        />
      </HalfContainer>
      <FullContainer>
        <SectionContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="adminId"
              label={`${t('common:admin')} ${t('common:tour')}`}
              placeholder={`${t('common:admin')} ${t('common:tour')}`}
              disabled
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="operatorId"
              label={t('common:operator')}
              placeholder={t('common:operator')}
              disabled
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="salesmanId"
              label={t('common:sales')}
              placeholder={t('common:sales')}
              disabled={tourType === 'private'}
            />
          </OneThirdContainer>
        </SectionContainer>
      </FullContainer>
      <FullContainer>
        <SectionContainer>
          <HalfContainer>
            <CustomerSelectInput
              name="customerId"
              onAfterDetailChange={onAfterDetailChangeCustomer}
              required
              isShowTransactionQuota
            />
          </HalfContainer>
          <HalfContainer>
            <CustomerBillingSelectInput name="customerBillingId" />
          </HalfContainer>
        </SectionContainer>
      </FullContainer>
      <FullContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="text"
              name="phone"
              label={t('common:phone_number')}
              placeholder={t('common:phone_number')}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text"
              name="address"
              label={t('common:address')}
              placeholder={t('common:address')}
              disabled
              noMargin
              fontCase="TitleCase"
            />
          </HalfContainer>
        </SectionContainer>
      </FullContainer>
      <CustomerSalesWarningToast />
    </CardSection>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});
